import { v4 as uuidv4 } from 'uuid';

import routes from 'js/core_pre_webpack/routes';
import { trackClick } from 'js/core_webpack/on_site_clicks';
import fetchWithCsrf from 'js/utils/fetch_with_csrf';
import ServerClientTime from './ServerClientTime';
import { bulkWrite } from 'js/core_webpack/arbitrary_events';

// https://github.com/academia-edu/wiki/blob/master/development/impressions.md

// This enum should be kept in sync with MODEL_NAME_ENUM on the backend
export enum ImpressionEntity {
  UnMapped = 0,
  User = 1,
  Work = 2,
  ResearchInterest = 3,
  InTextCitation = 4,
  News = 5,
  CuratedList = 6,
  KeyConcept = 7,
  Video = 8,
  Course = 9,
  LetterSubmission = 10,
  SurveyQuestion = 11,
  Survey = 12,
  LetterPeerReviewModal = 13,
  LetterPeerReviewForm = 14,
  CitationModal = 15,
  OpenalexWork = 16,
  AttachmentPage = 17,
  Comment = 18,
  SocialTopic = 19,
  GrantOpportunityProject = 20,
  PaperSet = 21,
}

type Entries = IntersectionObserverEntry[];

type Data = {
  impression_id: string;
  entity_id: number | string;
  entity_type: number;
  source: string;
  extra?: object;
};

type TimingData = {
  source: string;
  entity_id: number | string;
  entity_type: number;
  total_duration_ms: number;
};

export type Options = {
  entity_id?: number | string;
  entity_type?: ImpressionEntity;
  extra?: object;
  // NOTE: source must be <= 64 characters due to redshift column size.
  // Longer strings will be truncated
  source: string;
  threshold?: number;
  impressionId?: string;
  trackDimensions?: boolean;
  // WARNING: This should be used carefully and sparingly.
  // This disables batch sending of impressions, which results
  // in a more accurate count because we don't lose impressions
  // when the user navigates away, but at the cost of performance.
  noBatch?: boolean;
  // Callback when an impression is logged
  onImpression?: () => void;
};

export interface Watcher {
  // Call to have the watcher record the click
  click: () => void;
  // If the element this watcher is watching has already been seen
  hasImpressed: () => boolean;
  // Manually stop watching for impressions
  stop: () => void;
  impressionId: string;
}

// This is a singleton all impression watchers use to determine
// if the element they're watching has already intersected.
// This prevents us from sending duplicate impressions
const intersected = new Set();

// Only run in browser environment, not during SSR
if (typeof window !== 'undefined') {
  const domain = window.$domain;
  const expires = 'Thu, 01 Jan 1970 00:00:00 GMT'; // Effectively deletes the cookie
  document.cookie = `js_impression_nav_from=; expires=${expires}; path=/; domain=${domain}`;
}

// Does not check if this is the first view, sends data indiscriminately.
export const sendBatchImpressions = (data: Data[]) => {
  const serverClientTime = new ServerClientTime();
  fetchWithCsrf(routes.api_v0_views_path({ subdomain_param: 'api' }), {
    method: 'POST',
    body: JSON.stringify({ views: data }),
    headers: {
      'Content-Type': 'application/json',
    },
    // keepalive is important when sending calls and the page is unloading
    // https://developer.mozilla.org/en-US/docs/Web/API/RequestInit#keepalive
    keepalive: true,
  }).then((r) => serverClientTime.update(r));
};

export const sendBatchTimings = (data: TimingData[]) => {
  bulkWrite('SocialNewsTiming', data);
};

// Maximum number of impressions to collect/send in a single request
const MAX_VIEW_WINDOW = 50;
// Send data every 5 seconds if we don't have a full window of events
const THROTTLE_TIMEOUT = 5;
export class BatchImpressionReporter<T> {
  views: T[];
  timerId: number | null;
  flusher: { (items: T[]): void };
  maxViewWindow: number;
  throttleTimeoutInSeconds: number;

  constructor(
    flusher: { (items: T[]): void },
    maxViewWindow: number = MAX_VIEW_WINDOW,
    throttleTimeoutInSeconds: number = THROTTLE_TIMEOUT,
  ) {
    this.views = [];
    this.timerId = null;
    this.flusher = flusher;
    this.maxViewWindow = maxViewWindow;
    this.throttleTimeoutInSeconds = throttleTimeoutInSeconds;

    // Force cleanup of any pending events when leaving page
    // See https://www.igvita.com/2015/11/20/dont-lose-user-and-app-state-use-page-visibility/
    // for why this uses a combination of pagehide and visibilitychange
    // Only add event listeners in browser environment, not during SSR
    if (typeof document !== 'undefined') {
      document.addEventListener('pagehide', () => this.flush());
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') {
          this.flush();
        }
      });
    }
  }

  _clearThrottle() {
    if (this.timerId !== null) {
      window.clearTimeout(this.timerId);
      this.timerId = null;
    }
  }

  flush() {
    const impressionsToSend = this.views;
    this.views = [];
    this._clearThrottle();

    if (impressionsToSend.length) {
      this.flusher(impressionsToSend);
    }
  }

  softFlush() {
    if (this.views.length >= this.maxViewWindow) {
      this.flush();
    } else {
      // Set a timer unless one is already set.
      if (this.timerId === null) {
        this.timerId = window.setTimeout(
          () => this.flush(),
          this.throttleTimeoutInSeconds * 1000,
        );
      }
    }
  }

  registerView(view: T, sendImmediately = false) {
    if (sendImmediately) {
      this.flusher([view]);
      return;
    }

    this.views.push(view);
    this.softFlush();
  }
}
// Create a singleton watcher to collect and batch all requests
const batchUploader = new BatchImpressionReporter<Data>((items: Data[]) =>
  sendBatchImpressions(items),
);

// Create a singleton watcher to collect and batch all timing requests
const timingBatchUploader = new BatchImpressionReporter<TimingData>(
  (items: TimingData[]) => sendBatchTimings(items),
);

const setClickCookie = (id: number | string) => {
  // Skip during server-side rendering
  if (typeof window === 'undefined') {
    return;
  }

  const maxAgeSeconds = 600; // 10 minutes
  const domain = window.$domain;
  document.cookie = `js_impression_nav_from=${id}; Max-Age=${maxAgeSeconds}; path=/; domain=${domain}`;
};

/**
 * Uses the Intersection Observer API to watch impressions for the given element.
 * When the element scrolls into view by a certain amount (adjustable by `options.threshold`),
 * this records an impression to the server with the given data.
 *
 * Impressions are only recorded the first time the user sees something.
 * Once we record an impression for a particular element,
 * we disconnect the observer and stop watching since we don't care about subsequent calls.
 */
export const watchImpressions = (
  element: HTMLElement,
  options: Options,
): Watcher => {
  const {
    entity_id = '0',
    entity_type = 0,
    extra = {},
    source,
    threshold = 0.05,
    trackDimensions = false, // Track several dimensions too (width, height, page dimensions)
    onImpression,
  } = options;
  const impression_id = options.impressionId ?? getImpressionId();
  const data = {
    impression_id,
    entity_id,
    entity_type,
    source,
    extra,
  };
  const impressionKey = `${source}:${entity_type}:${entity_id}`;

  if (source?.length > 64) {
    // eslint-disable-next-line no-console
    console.warn(
      `Impression source '${source} is > 64 characters and will be truncated in redshift`,
    );
  }

  // IntersectionObserver.takeRecords() returns entries that have changed
  // since the last check, so this takes care of the (probably rather rare)
  // cases in which the observed element hasn't been reported as intersecting.
  const doubleCheckIntersection = (): void =>
    handleIntersection(observer.takeRecords());

  const hasImpressed = (): boolean => intersected.has(impressionKey);

  const handleIntersection = (entries: Entries): void => {
    // For this to happen, an element with the same key as another will have had
    // to have mounted before the other reported as intersected. I don't really
    // think that will happen, but whatever, just in case:
    if (hasImpressed()) {
      observer.disconnect();
      return;
    }

    const serverClientTime = new ServerClientTime();
    // Test with only social impressions
    const difference = data?.source?.match(/^soc_/)
      ? serverClientTime.difference
      : 0;
    const impression_time = new Date(Date.now() - difference);
    // @ts-ignore
    const hit_id =
      (window.Aedu && window.Aedu.hit_data && window.Aedu.hit_data.id) || null;
    const annotatedViewData = { ...data, impression_time, hit_id };

    if (entries.length === 0) {
      batchUploader.registerView(annotatedViewData, options.noBatch);
      observer.disconnect();
    } else if (entries[0].isIntersecting) {
      if (trackDimensions) {
        const rect = entries[0].boundingClientRect;

        // Round everything that needs it, because floats can throw backend validation errors
        annotatedViewData.extra = {
          ...annotatedViewData.extra,
          ww: document.documentElement.clientWidth,
          wh: document.documentElement.clientHeight,
          sw: Math.round(window.screen.width),
          sh: Math.round(window.screen.height),
          sx: Math.round(window.scrollX),
          sy: Math.round(window.scrollY),
          ew: Math.round(rect.width),
          eh: Math.round(rect.height),
          ex: Math.round(rect.left),
          ey: Math.round(rect.top),
        };
      }

      intersected.add(impressionKey);
      batchUploader.registerView(annotatedViewData, options.noBatch);
      onImpression?.();
      // We only care about the first impression that happens for a particular
      // element, so point in continuing to check.
      observer.disconnect();
    }
  };

  const observer = new IntersectionObserver(handleIntersection, { threshold });
  if (!hasImpressed()) {
    observer.observe(element);
  }

  return {
    hasImpressed,
    // Manually record a click using the impression watcher
    click: () => {
      doubleCheckIntersection();
      trackClick(`impression-watched:${impressionKey}`);
      setClickCookie(impression_id);
    },
    // Manually stop watching for impressions
    stop: () => observer.disconnect(),
    impressionId: impression_id,
  };
};

export const getImpressionId: () => string = uuidv4;

export function flushImpressions() {
  batchUploader.flush();
}

export interface TimingWatcher {
  // Manually stop watching for timings
  stop: () => void;
}

export type TimingOptions = {
  entity_id?: number | string;
  entity_type?: ImpressionEntity;
  source: string;
  threshold?: number;
};

/**
 * Uses the Intersection Observer API to track how long an element is visible in the viewport.
 * When the element scrolls into view by a certain amount (adjustable by `options.threshold`),
 * this starts tracking time. Time is only tracked when the tab is active.
 *
 * The watcher sends timing data when:
 * - The element goes out of view
 * - The tab becomes hidden
 * - The page unloads
 * - The watcher is stopped
 *
 * Each timing payload includes the total duration the element has been visible.
 */
export const watchTimings = (
  element: HTMLElement,
  options: TimingOptions,
): TimingWatcher => {
  const {
    entity_id = '0',
    entity_type = 0,
    source,
    threshold = 0.05,
  } = options;

  let startTime: number | null = null;
  let totalDuration = 0;
  let isIntersecting = false;
  let isTabVisible = !document.hidden;

  const sendTiming = () => {
    if (!startTime) return;

    const currentDuration = Date.now() - startTime;
    totalDuration += currentDuration;

    const timingData: TimingData = {
      source,
      entity_id,
      entity_type,
      total_duration_ms: totalDuration,
    };

    timingBatchUploader.registerView(timingData);
    startTime = null;
  };

  // Handle tab visibility changes
  const handleVisibilityChange = () => {
    isTabVisible = document.visibilityState === 'visible';
    if (isTabVisible && isIntersecting && !startTime) {
      startTime = Date.now();
    } else if (!isTabVisible) {
      sendTiming();
    }
  };

  // Handle page unload
  const handlePageHide = () => {
    sendTiming();
  };

  document.addEventListener('visibilitychange', handleVisibilityChange);
  document.addEventListener('pagehide', handlePageHide);

  if (source?.length > 64) {
    // eslint-disable-next-line no-console
    console.warn(
      `Timing source '${source} is > 64 characters and will be truncated in redshift`,
    );
  }

  const handleIntersection = (entries: Entries): void => {
    isIntersecting = entries[0]?.isIntersecting ?? false;

    if (isIntersecting && isTabVisible && !startTime) {
      startTime = Date.now();
    } else if (!isIntersecting && startTime) {
      sendTiming();
    }
  };

  const observer = new IntersectionObserver(handleIntersection, { threshold });
  observer.observe(element);

  return {
    stop: () => {
      sendTiming();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      document.removeEventListener('pagehide', handlePageHide);
      observer.disconnect();
    },
  };
};
